// Automation settings: which bookkeeping jobs run on their own each night.
// Owner-only on the server; everything starts switched off.
import { useEffect, useState } from "react";
import {
  type AutomationSettings,
  getAutomationSettings,
  updateAutomationSettings,
} from "@/lib/api";
import { useOrganization } from "@/lib/organization";

type Flag = keyof Omit<AutomationSettings, "last_run_on">;

const JOBS: Array<{ flag: Flag; label: string; description: string }> = [
  {
    flag: "bookkeeping_sweep",
    label: "Post depreciation and loan payments",
    description:
      "Each night, post the journals for depreciation and loan payments that are due.",
  },
  {
    flag: "recurring_generation",
    label: "Create recurring invoices, bills and journals",
    description:
      "Each night, create the recurring documents whose date has arrived.",
  },
  {
    flag: "payment_reminders",
    label: "Send payment reminders",
    description:
      "Each night, email customers according to your reminder rules. This sends real email.",
  },
  {
    flag: "stripe_retry",
    label: "Retry failed payment postings",
    description:
      "Each night, retry online payments that could not be recorded in your books.",
  },
];

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

export function AutomationTab() {
  const { scope } = useOrganization();
  const organizationId = scope?.organization_id;
  const [settings, setSettings] = useState<AutomationSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Flag | null>(null);

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    getAutomationSettings(organizationId)
      .then((value) => {
        if (!cancelled) setSettings(value);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(toErrorMessage(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const toggle = async (flag: Flag, next: boolean) => {
    if (!organizationId) return;
    setBusy(flag);
    setError(null);
    try {
      setSettings(
        await updateAutomationSettings(organizationId, { [flag]: next }),
      );
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="grid gap-4">
      <p className="text-sm text-[var(--muted-foreground)]">
        Choose which bookkeeping jobs run on their own each night (early morning
        UTC). Nothing runs until you turn it on, and only the organization owner can
        change these.
      </p>
      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
        </div>
      ) : null}
      {settings ? (
        <ul className="grid gap-3">
          {JOBS.map((job) => (
            <li key={job.flag} className="surface rounded-xl p-4">
              <label
                htmlFor={`automation-${job.flag}`}
                className="flex items-start gap-3 text-sm"
              >
                <input
                  id={`automation-${job.flag}`}
                  type="checkbox"
                  className="mt-1"
                  checked={settings[job.flag]}
                  disabled={busy !== null}
                  onChange={(event) =>
                    void toggle(job.flag, event.target.checked)
                  }
                />
                <span className="grid min-w-0 gap-1">
                  <span className="break-words font-medium">{job.label}</span>
                  <span className="break-words text-[var(--muted-foreground)]">
                    {job.description}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      ) : error ? null : (
        <p className="text-sm text-[var(--muted-foreground)]">Loading...</p>
      )}
      {settings?.last_run_on ? (
        <p className="text-xs text-[var(--muted-foreground)]">
          Last nightly run finished on {settings.last_run_on}.
        </p>
      ) : null}
    </div>
  );
}
